import React, {Fragment, PureComponent, ReactNode, MouseEvent as ReactMouseEvent, TouchEvent as ReactTouchEvent,
  RefObject, createRef} from 'react';
import {SpacePricingCalendar} from 'services/entities/pricing';
import {getTimePrice, loadTaxes} from 'services/pricing';
import {ExpandUpIcon, ExpandDownIcon} from 'components/Icon';
import {CurrencyAmount} from 'util/currency';
import {DateTime, TimeSpan} from 'util/time';
import {BookingSettings} from 'services/entities/booking';
import {loadSettings} from 'services/pricing';
import {handleError} from 'util/errors';
import {TaxType} from 'services/entities/taxes';
import {applyTaxes} from 'util/pricing';
import './index.scss';

interface BookingTimeValue {
  start:TimeSpan;
  end:TimeSpan;
}

interface BookingTimePickerProps {
  className?: string;
  pricingCalendar?: SpacePricingCalendar;
  reservedTimes?:TimeRange[];
  selectedTimes?:TimeRange[];
  date?: DateTime;
  onChange?: (value:BookingTimeValue|null)=>void;
  value?: BookingTimeValue;
  disabled?: boolean;
}

interface BookingTimePickerState {
  settings: BookingSettings|null;
  taxes: TaxType[]|null;
}

enum TimeStatus {
  Available,
  Selected,
  Current,
  Unavailable,
  Reserved
}

export interface TimeRange {
  start: TimeSpan;
  end: TimeSpan;
}

export default class BookingTimePicker extends PureComponent<BookingTimePickerProps, BookingTimePickerState> {
  state: BookingTimePickerState = {
    settings: null,
    taxes: null
  };

  private containerRef:RefObject<HTMLDivElement> = createRef();
  private selectionDragStartY: number = 0;
  private selectionDragOffsetY: number = 0;

  componentDidMount(): void {
    loadSettings().then(
      settings => this.setState({settings: settings}),
      ex => handleError('Failed to load booking settings', ex)
    );
    const calendar = this.props.pricingCalendar;
    if(calendar) {
      loadTaxes(calendar.currency).then(
        taxes => this.setState({taxes: taxes}),
        ex => handleError('Failed to load taxes', ex)
      );
    }
  }

  componentDidUpdate(prevProps: Readonly<BookingTimePickerProps>, prevState: Readonly<BookingTimePickerState>, snapshot?: any): void {
    if(prevProps.pricingCalendar !== this.props.pricingCalendar) {
      this.setState({taxes: null});
      const calendar = this.props.pricingCalendar;
      if(calendar) {
        loadTaxes(calendar.currency).then(
          taxes => this.setState({taxes: taxes}),
          ex => handleError('Failed to load taxes', ex)
        );
      }
    }
  }

  getTimePrice(time: TimeSpan): CurrencyAmount|null {
    if(!this.props.pricingCalendar || !this.props.date) {
      return null;
    }
    return getTimePrice(this.props.date.add(time), this.props.pricingCalendar);
  }

  getTimeStatus(time: TimeSpan, price: CurrencyAmount|null,
                       selectionStart:TimeSpan|null = null, selectionEnd:TimeSpan|null = null): TimeStatus {
    if (!price) {
      return TimeStatus.Unavailable;
    } else if(this.isReservedTime(time)) {
      return TimeStatus.Reserved;
    } else if(this.isSelectedTime(time)) {
      return TimeStatus.Selected;
    } else if(selectionStart && selectionEnd && time.isBetween(selectionStart, selectionEnd, true, false)) {
      return TimeStatus.Current;
    } else {
      return TimeStatus.Available;
    }
  }

  isReservedTime(time: TimeSpan): boolean {
    const reservedTimes = this.props.reservedTimes;
    if(!reservedTimes) {
      return false;
    }
    return !!reservedTimes.find(t => time.isBetween(t.start, t.end, true, true));
  }

  isSelectedTime(time: TimeSpan): boolean {
    const selectedTimes = this.props.selectedTimes;
    if(!selectedTimes) {
      return false;
    }
    return !!selectedTimes.find(t => time.isBetween(t.start, t.end, true, false));
  }

  private selectTime( start: TimeSpan, end: TimeSpan) {
    const settings = this.state.settings;
    if(!settings) {
      console.warn('Settings not loaded');
      return;
    }
    const currentStart = this.props.value ? this.props.value.start : null;
    const currentEnd = this.props.value ? this.props.value.end : null;
    const selected = currentStart && !currentStart.greaterThan(start) && currentEnd && !currentEnd.lessThan(end);
    let newStart = currentStart;
    let newEnd = currentEnd;
    if(!selected || !currentStart || !currentEnd) {
      // currently deselected - select this time
      newStart = start;
      newEnd = end;
      if (newEnd.subtract(newStart).lessThan(settings.minDuration)) {
        // smaller than minimum interval - move out end time
        newEnd = newStart.add(settings.minDuration);
      }
      // ensure end doesn't overlap with unavailable times
      newEnd = this.getLastAvailableTime(newStart, newEnd, settings.increment);
      if (newEnd.subtract(newStart).lessThan(settings.minDuration)) {
        // smaller than minimum interval - move up start time
        newStart = newEnd.subtract(settings.minDuration);
      }
      // ensure start doesn't overlap with unavailable times
      newStart = this.getFirstAvailableTime(newStart, newEnd, settings.increment);
      if (newEnd.subtract(newStart).lessThan(settings.minDuration)) {
        // smaller than minimum interval - selection not possible
        return;
      }
      if (this.props.onChange) {
        this.props.onChange(newStart && newEnd ? {start: newStart, end: newEnd} : null);
      }
    }
  }

  getNearestSlotIndex(y: number, useMiddle: boolean = true): number {
    if(!this.containerRef.current) {
      return 0;
    }
    const slotRows = this.containerRef.current.querySelectorAll('.slot');
    let nearestIndex = 0;
    let nearestOffsetY = -1;
    for(let k = 0; k < slotRows.length; k++) {
      const bounds = slotRows[k].getBoundingClientRect();
      const centerY = useMiddle ? bounds.y + bounds.height * 0.5 : bounds.y;
      const offsetY = Math.abs(centerY - y);
      if(nearestOffsetY < 0 || offsetY < nearestOffsetY) {
        nearestOffsetY = offsetY;
        nearestIndex = k;
      }
    }
    return nearestIndex;
  }

  onRowMouseDown = (ev:ReactMouseEvent, start: TimeSpan, end: TimeSpan) => {
    ev.preventDefault();
    ev.stopPropagation();
    const bounds = ev.currentTarget.getBoundingClientRect();
    this.selectionDragStartY = ev.clientY;
    this.selectionDragOffsetY = bounds.y - ev.clientY;
    document.addEventListener('mousemove', this.onRowMouseMove);
    document.addEventListener('mouseup', this.onRowMouseUp);
    this.selectTime(start, end);
  };

  onRowTouchStart = (ev: ReactTouchEvent, start: TimeSpan, end: TimeSpan) => {
    ev.preventDefault();
    ev.stopPropagation();
    const bounds = ev.currentTarget.getBoundingClientRect();
    const touch = ev.touches[0];
    this.selectionDragStartY = touch.clientY;
    this.selectionDragOffsetY = bounds.y - touch.clientY;
    const el = ev.currentTarget as HTMLElement;
    el.addEventListener('touchmove', this.onRowTouchMove);
    el.addEventListener('touchend', this.onRowTouchEnd);
    this.selectTime(start, end);
  };

  onRowMouseMove = (ev: MouseEvent) => {
    ev.preventDefault();
    ev.stopPropagation();
    if(ev.clientY < this.selectionDragStartY) {
      this.expandSelectionUp(ev.clientY);
      this.expandSelectionDown(this.selectionDragStartY);
    } else {
      this.expandSelectionDown(ev.clientY);
      this.expandSelectionUp(this.selectionDragStartY);
    }
  };

  onRowTouchMove = (ev: TouchEvent) => {
    ev.preventDefault();
    ev.stopPropagation();
    const y = ev.touches[0].clientY;
    if(y < this.selectionDragStartY) {
      this.expandSelectionUp(y);
      this.expandSelectionDown(this.selectionDragStartY);
    } else {
      this.expandSelectionDown(y);
      this.expandSelectionUp(this.selectionDragStartY);
    }
  };

  onRowMouseUp = () => {
    document.removeEventListener('mousemove', this.onRowMouseMove);
    document.removeEventListener('mouseup', this.onRowMouseUp);
  };

  onRowTouchEnd = (ev: TouchEvent) => {
    const el = ev.currentTarget as HTMLElement;
    el.removeEventListener('touchmove', this.onRowTouchMove);
    el.removeEventListener('touchend', this.onRowTouchEnd);
  };

  onSelectionExpandUpMouseDown = (ev: ReactMouseEvent) => {
    ev.preventDefault();
    ev.stopPropagation();
    document.addEventListener('mousemove', this.onSelectionExpandUpMouseMove);
    document.addEventListener('mouseup', this.onDocumentMouseUp);
  };

  onSelectionExpandUpTouchStart = (ev: ReactTouchEvent) => {
    ev.preventDefault();
    ev.stopPropagation();
    const el = ev.currentTarget as HTMLElement;
    el.addEventListener('touchmove', this.onSelectionExpandUpTouchMove);
    el.addEventListener('touchend', this.onSelectionTouchEnd);
  };

  onSelectionExpandDownMouseDown = (ev: ReactMouseEvent) => {
    ev.preventDefault();
    ev.stopPropagation();
    document.addEventListener('mousemove', this.onSelectionExpandDownMouseMove);
    document.addEventListener('mouseup', this.onDocumentMouseUp);
  };

  onSelectionExpandDownTouchStart = (ev: ReactTouchEvent) => {
    ev.preventDefault();
    ev.stopPropagation();
    const el = ev.currentTarget as HTMLElement;
    el.addEventListener('touchmove', this.onSelectionExpandDownTouchMove);
    el.addEventListener('touchend', this.onSelectionTouchEnd);
  };

  onSelectionMouseDown = (ev: ReactMouseEvent) => {
    ev.preventDefault();
    const bounds = ev.currentTarget.getBoundingClientRect();
    this.selectionDragOffsetY = bounds.y - ev.clientY;
    document.addEventListener('mousemove', this.onSelectionMouseMove);
    document.addEventListener('mouseup', this.onDocumentMouseUp);
  };

  onSelectionTouchStart = (ev: ReactTouchEvent) => {
    ev.preventDefault();
    ev.stopPropagation();
    const el = ev.currentTarget as HTMLElement;
    const bounds = el.getBoundingClientRect();
    this.selectionDragOffsetY = bounds.y - ev.touches[0].clientY;
    el.addEventListener('touchmove', this.onSelectionTouchMove);
    el.addEventListener('touchend', this.onSelectionTouchEnd);
  };

  onSelectionExpandUpMouseMove = (ev: MouseEvent) => {
    this.expandSelectionUp(ev.clientY);
  };

  onSelectionExpandUpTouchMove = (ev: TouchEvent) => {
    const y = (ev as TouchEvent).touches[0].clientY;
    this.expandSelectionUp(y);
  };

  onSelectionExpandDownMouseMove = (ev: MouseEvent) => {
    this.expandSelectionDown(ev.clientY);
  };

  onSelectionExpandDownTouchMove = (ev: TouchEvent) => {
    ev.preventDefault();
    this.expandSelectionDown(ev.touches[0].clientY);
  };

  onSelectionMouseMove = (ev: MouseEvent) => {
    this.moveSelection(ev.clientY);
  };

  onSelectionTouchMove = (ev: TouchEvent) => {
    ev.preventDefault();
    this.moveSelection(ev.touches[0].clientY);
  };

  onDocumentMouseUp = () => {
    document.removeEventListener('mousemove', this.onSelectionExpandUpMouseMove);
    document.removeEventListener('mousemove', this.onSelectionExpandDownMouseMove);
    document.removeEventListener('mousemove', this.onSelectionMouseMove);
    document.removeEventListener('mouseup', this.onDocumentMouseUp);
  };

  onSelectionTouchEnd = (ev: TouchEvent) => {
    const el = ev.currentTarget as HTMLElement;
    if(!el) {
      return;
    }
    el.removeEventListener('touchmove', this.onSelectionExpandUpTouchMove);
    el.removeEventListener('touchmove', this.onSelectionExpandDownTouchMove);
    el.removeEventListener('touchmove', this.onSelectionTouchMove);
    el.removeEventListener('touchend', this.onSelectionTouchEnd);
  };

  expandSelectionUp(mouseY: number) {
    const nearestSlotIndex = this.getNearestSlotIndex(mouseY);
    const value = this.props.value;
    if(!value) {
      console.warn('Moving selection without selection');
      return;
    }
    const settings = this.state.settings;
    if(!settings) {
      console.warn('Settings not loaded');
      return;
    }
    let newStart = settings.minTime.add(settings.increment.multiply(nearestSlotIndex));
    const newEnd = value.end;
    if(newEnd.subtract(newStart).lessThan(settings.minDuration)) {
      newStart = newEnd.subtract(settings.minDuration);
    }
    newStart = this.getFirstAvailableTime(newStart, newEnd, settings.increment);
    if(!value || !value.start.equals(newStart)) {
      if(this.props.onChange) {
        this.props.onChange({start: newStart, end: newEnd});
      }
    }
  }

  expandSelectionDown(mouseY: number) {
    const nearestSlotIndex = this.getNearestSlotIndex(mouseY);
    const value = this.props.value;
    if(!value) {
      console.warn('Moving selection without selection');
      return;
    }
    const settings = this.state.settings;
    if(!settings) {
      console.warn('Settings not loaded');
      return;
    }
    let newEnd = settings.minTime.add(settings.increment.multiply(nearestSlotIndex + 1));
    const newStart = value.start;
    if(newEnd.subtract(newStart).lessThan(settings.minDuration)) {
      newEnd = newStart.add(settings.minDuration);
    }
    newEnd = this.getLastAvailableTime(newStart, newEnd, settings.increment);
    if(!value || !value.end.equals(newEnd)) {
      if(this.props.onChange) {
        this.props.onChange({start: newStart, end: newEnd});
      }
    }
  }

  moveSelection(mouseY: number) {
    const nearestSlotIndex = this.getNearestSlotIndex(mouseY + this.selectionDragOffsetY, false);
    const value = this.props.value;
    if(!value) {
      console.warn('Moving selection without selection');
      return;
    }
    const settings = this.state.settings;
    if(!settings) {
      console.warn('Settings not loaded');
      return;
    }
    const duration = value.end.subtract(value.start);
    let newStart = settings.minTime.add(settings.increment.multiply(nearestSlotIndex));
    newStart = this.getFirstAvailableTime(newStart, value.end, settings.increment);
    let newEnd = newStart.add(duration);
    newEnd = this.getLastAvailableTime(newStart, newEnd, settings.increment);
    if(newEnd.subtract(newStart).lessThan(duration)) {
      newStart = newEnd.subtract(duration);
    }
    if(!value || !value.start.equals(newStart)) {
      if(this.props.onChange) {
        this.props.onChange({start: newStart, end: newEnd});
      }
    }
  }

  getFirstAvailableTime(start: TimeSpan, end: TimeSpan, increment: TimeSpan): TimeSpan {
    let time = end.subtract(increment);
    while(!time.lessThan(start)) {
      const status = this.getTimeStatus(time, this.getTimePrice(time));
      if(status !== TimeStatus.Available) {
        return time.add(increment);
      }
      time = time.subtract(increment);
    }
    return start;
  }

  getLastAvailableTime(start: TimeSpan, end: TimeSpan, increment: TimeSpan): TimeSpan {
    let time = start.clone();
    while(!time.greaterThan(end)) {
      const status = this.getTimeStatus(time, this.getTimePrice(time));
      if(status !== TimeStatus.Available) {
        return time;
      }
      time = time.add(increment);
    }
    return end;
  }

  renderRows(): ReactNode[] {
    const settings = this.state.settings;
    if(!settings) {
      return [];
    }
    let rows:ReactNode[] = [];
    const minTime = settings.minTime;
    const maxTime = settings.maxTime;
    let time = minTime;
    let lastRowSpan = 0;
    const rowInterval = settings.priceDuration;
    let rowIndex = 1;
    const rowSpan = Math.round(rowInterval.totalSeconds / settings.increment.totalSeconds);
    while(time.lessThan(maxTime)) {
      const nextTime = time.add(rowInterval);
      const price = this.getTimePrice(time);
      rows.push(<span key={`time-${rowIndex}`} className="time" style={{gridRow: `${rowIndex} / span ${rowSpan}`}}>{ time.format('h:mmA') }</span>);
      const result = this.renderRowTimeSlots(time, nextTime, maxTime, settings.increment, settings.priceDuration,
        price, rowIndex, lastRowSpan);
      const priceVisible = price && this.props.reservedTimes !== undefined &&
        (result.lastStatus === TimeStatus.Available || result.lastStatus === TimeStatus.Selected || result.lastStatus === TimeStatus.Current);
      rows.push(<span key={`price-${rowIndex}`} className="price" style={{gridRow: `${rowIndex} / span ${rowSpan}`}}>{priceVisible ? price!.format('$0') : '-' }</span>);
      rowIndex = result.rowIndex;
      lastRowSpan = result.lastRowSpan;
      rows = rows.concat(result.elements);
      time = nextTime;
    }
    return rows;
  }

  getRowInfo(time: TimeSpan, price: CurrencyAmount|null, maxTime: TimeSpan, increment: TimeSpan, priceDuration: TimeSpan,
             selectionStart:TimeSpan|null, selectionEnd:TimeSpan|null):
      {rowSpan: number, status: TimeStatus, spanPrice: CurrencyAmount|null} {
    const status = this.getTimeStatus(time, price, selectionStart, selectionEnd);
    if(status === TimeStatus.Available) {
      return {rowSpan: 1, status: status, spanPrice: price};
    }
    let spanPrice = price ? price : null;
    let rowSpan = 1;
    time = time.add(increment);
    while(time.lessThan(maxTime)) {
      const nextPrice = this.getTimePrice(time);
      const nextStatus = this.getTimeStatus(time, nextPrice, selectionStart, selectionEnd);
      if(nextStatus !== status) {
        break;
      }
      if(nextPrice) {
        spanPrice = spanPrice ? spanPrice.add(nextPrice) : nextPrice;
      }
      rowSpan++;
      time = time.add(increment);
    }
    if(spanPrice) {
      // time slots are 30 minutes, while prices are for an hour, so need to scale price
      spanPrice = spanPrice.multiply(increment.totalSeconds / priceDuration.totalSeconds);
    }
    return {rowSpan: rowSpan, status: status, spanPrice: spanPrice};
  }

  renderBookingPrice(price: CurrencyAmount) {
    if(!this.state.taxes) {
      return price.format('$0.00');
    } else {
      price = applyTaxes(price, this.state.taxes);
      return <Fragment>{ price.format('$0.00') } <span className="vat-label">VAT Incl.</span></Fragment>;
    }
  }

  renderRowTimeSlots(startTime: TimeSpan, endTime: TimeSpan, maxTime: TimeSpan, increment: TimeSpan,
                     priceDuration: TimeSpan, price: CurrencyAmount|null, rowIndex: number,
                     lastRowSpan: number) : {rowIndex: number, lastRowSpan: number, lastStatus: TimeStatus, elements: ReactNode[]} {
    const selectionStart = this.props.value ? this.props.value.start : null;
    const selectionEnd = this.props.value ? this.props.value.end : null;
    const elements: ReactNode[] = [];
    let time = startTime;
    let lastStatus: TimeStatus = TimeStatus.Unavailable;
    while(time.lessThan(endTime)) {
      let {rowSpan, status, spanPrice} = this.getRowInfo(time, price, maxTime, increment, priceDuration,
        selectionStart, selectionEnd);
      if (lastRowSpan > 0) {
        rowSpan = -1;
      }
      const start = time.clone();
      const end = time.add(increment);
      const spanEnd = time.add(increment.multiply(rowSpan));
      let statusClassName = '';
      let content: ReactNode = null;
      switch (status) {
        default:
        case TimeStatus.Unavailable:
        case TimeStatus.Reserved:
          statusClassName = 'unavailable';
          content = 'Unavailable';
          break;
        case TimeStatus.Selected:
          statusClassName = 'selected';
          content = <Fragment>
            <span className="time">{`${time.format('h:mmA')} - ${spanEnd.format('h:mmA')}`}</span>
            <span className="price">{spanPrice ? this.renderBookingPrice(spanPrice) : ''}</span>
          </Fragment>;
          break;
        case TimeStatus.Current:
          statusClassName = 'current';
          content = <Fragment>
            <button className="resize-start" onMouseDown={this.onSelectionExpandUpMouseDown}
                    onTouchStart={this.onSelectionExpandUpTouchStart}><ExpandUpIcon/></button>
            <span className="time">{`${selectionStart!.format('h:mmA')} - ${selectionEnd!.format('h:mmA')}`}</span>
            <span className="price">{spanPrice ? this.renderBookingPrice(spanPrice) : ''}</span>
            <button className="resize-end" onMouseDown={this.onSelectionExpandDownMouseDown}
                    onTouchStart={this.onSelectionExpandDownTouchStart}><ExpandDownIcon/></button>
          </Fragment>;
          break;
        case TimeStatus.Available:
          statusClassName = 'available';
          break;
      }
      let onMouseDown: ((ev:ReactMouseEvent)=>void)|undefined = undefined;
      let onTouchStart: ((ev:ReactTouchEvent)=>void)|undefined = undefined;
      if(status === TimeStatus.Available || status === TimeStatus.Current) {
        onMouseDown = (ev: ReactMouseEvent) => this.onRowMouseDown(ev, start, end);
        onTouchStart = (ev: ReactTouchEvent) => this.onRowTouchStart(ev, start, end);
      }
      elements.push(<span key={`slot-${rowIndex}`} className={`slot ${statusClassName}`} onMouseDown={onMouseDown}
                          onTouchStart={onTouchStart} style={{gridRow: rowIndex}} />);
      if (rowSpan > 0 && status !== TimeStatus.Available) {
        elements.push(<span key={`status-${rowIndex}`} className={`status ${statusClassName}`}
                            style={{gridRow: `${rowIndex} / span ${rowSpan}`}} onMouseDown={this.onSelectionMouseDown}
                            onTouchStart={this.onSelectionTouchStart}>
          {content}
        </span>);
      }
      time = time.add(increment);
      lastRowSpan = rowSpan > 0 ? rowSpan - 1 : lastRowSpan - 1;
      lastStatus = status;
      rowIndex++;
    }
    return { rowIndex: rowIndex, lastRowSpan: lastRowSpan, lastStatus: lastStatus, elements: elements };
  }

  render() {
    let fullClassName = 'booking-time-picker';
    if(this.props.className) {
      fullClassName += ' ' + this.props.className;
    }
    if(this.props.disabled) {
      fullClassName += ' disabled';
    }
    return <div className={fullClassName}>
      <div className="times" ref={this.containerRef}>
          { this.renderRows() }
      </div>
    </div>;
  }
}